import { CreditPayment, PaymentOrder, Ticket } from "../../../modules/administration"
import { deliveryHelpers, DeliveryOrder, Delivery } from "../../../modules/deliveries"
import { FiscalDocument, FiscalDocumentType } from "../../../modules/fiscal-documents"
import { SaleOperation, saleOperationHelpers } from "../../../modules/sales"
import { TableName } from "../../../tables"
import { FileType } from "../../ui"

export const fileHelpers = {
    ticketFileCode: (
        ticketCode?: number,
        parentCode?: number
    ) => {
        return `${ticketCode}-${parentCode}`
    },

    purchaseFileName: (purchaseCode: number): string => {
        return `Compra_${purchaseCode}`
    },
    purchaseTicketFileName: (
        ticketCode?: number,
        purchaseCode?: number
    ): string => {
        const justName = !ticketCode || !purchaseCode
        return justName ? `FacturaCompra` : `FacturaCompra_${fileHelpers.ticketFileCode(ticketCode, purchaseCode)}`
    },
    purchaseTicketSubPath: (
        ticket: Ticket,
        purchaseCode: number,
        ext = 'pdf'
    ): string => {
        return `tickets/${ticket.id}/${fileHelpers.purchaseTicketFileName(ticket.code, purchaseCode)}.${ext}`
    },

    expenseTicketFileName: (
        ticketCode?: number,
        expenseCode?: number
    ): string => {
        const justName = !ticketCode || !expenseCode
        return justName ? `FacturaGasto` : `FacturaGasto_${fileHelpers.ticketFileCode(ticketCode, expenseCode)}`
    },
    expenseTicketSubPath: (
        ticket: Ticket,
        expenseCode: number,
        ext = 'pdf'
    ): string => {
        return `tickets/${ticket.id}/${fileHelpers.expenseTicketFileName(ticket.code, expenseCode)}.${ext}`
    },

    salaryTicketFileName: (
        ticketCode?: number,
        salaryCode?: number
    ): string => {
        const justName = !ticketCode || !salaryCode
        return justName ? `ReciboSueldo` : `ReciboSueldo_${fileHelpers.ticketFileCode(ticketCode, salaryCode)}`
    },
    salaryTicketSubPath: (
        ticket: Ticket,
        salaryCode: number,
        ext = 'pdf'
    ): string => {
        return `tickets/${ticket.id}/${fileHelpers.salaryTicketFileName(ticket.code, salaryCode)}.${ext}`
    },

    deliveryNoteFileName2: (
        delivery?: Delivery,
        deliveryOrder?: DeliveryOrder
    ): string => {
        const fileLabel = deliveryHelpers.getDeliveryNoteLabel(delivery).replace(/ /g, '-').replace('ó', 'o')
        return `${fileLabel}_${deliveryHelpers.describeDeliveryCode(delivery, deliveryOrder)}`
    },
    deliveryNoteSubPath2: (
        delivery: Delivery,
        deliveryOrder?: DeliveryOrder,
        ext = 'pdf'
    ): string => {
        return `deliveries/${delivery.id}/deliveryNotes/${delivery.code}/${fileHelpers.deliveryNoteFileName2(delivery, deliveryOrder)}.${ext}`
    },
    
    deliveryNoteFileCode: (
        deliveryNoteCode: number,
        deliveryCode: number,
        operationCode: number
    ) => {
        return `${deliveryNoteCode}-${deliveryCode}-${operationCode}`
    },
    deliveryNoteFileName: (
        deliveryNoteCode?: number,
        deliveryCode?: number,
        operationCode?: number
    ): string => {
        const justName = !deliveryNoteCode || !deliveryCode || !operationCode
        return justName ? `Remito` : `Remito_${fileHelpers.deliveryNoteFileCode(deliveryNoteCode, deliveryCode, operationCode)}`
    },
    deliveryNoteSubPath: (
        deliveryNoteCode: number,
        delivery: Delivery,
        operationCode?: number,
        ext = 'pdf'
    ): string => {
        return `deliveries/${delivery.id}/deliveryNotes/${deliveryNoteCode}/${fileHelpers.deliveryNoteFileName(deliveryNoteCode, delivery.code, operationCode)}.${ext}`
    },

    paymentOrderFileName: (paymentOrderCode: number): string => {
        return `OrdenPago_${paymentOrderCode}`
    },
    paymentReceiptFileName: (paymentOrderCode: number): string => {
        return `ComprobantePago_${paymentOrderCode}`
    },
    paymentReceiptSubPath: (paymentOrder: PaymentOrder, ext = 'pdf'): string => {
        return `paymentOrders/${paymentOrder.id}/${fileHelpers.paymentReceiptFileName(paymentOrder.code)}.${ext}`
    },
    retentionCertificateFileName: (paymentOrderCode: number): string => {
        return `CertificadoRetencion_${paymentOrderCode}`
    },

    budgetFileName: (budgetCode: number): string => {
        return `Presupuesto_#${budgetCode}`
    },
    
    saleFileName: (saleCode: number): string => {
        return `Venta_#${saleCode}`
    },
    adjustmentNoteFileName: (adjustmentNoteCode: number, operationCode: number): string => {
        return `NotaCredito_#${adjustmentNoteCode}_${operationCode}`
    },

    hoardFileName: (hoardCode: number): string => {
        return `Acopio_#${hoardCode}`
    },
    hoardStatusFileName: (hoardCode: number): string => {
        return `EstadoAcopio_#${hoardCode}`
    },
    truckPreparationFileName: (roadmapCode: number): string => {
        return `PreparacionCamion_HR_#${roadmapCode}`
    },

    fiscalDocumentFileName: (fiscalDocument: FiscalDocument): string => {
        const { code, documentType } = fiscalDocument
        if (code) {
            return code.replace(' ', '_')
        }
        // Un comprobante sin número fiscal (p. ej. todavía no autorizado) no debe romper la
        // generación de nombres: usamos un nombre sin número y estable por tipo.
        const fallbackNames: Record<FiscalDocumentType, string> = {
            [FiscalDocumentType.INVOICE]: 'Factura',
            [FiscalDocumentType.CREDIT_NOTE]: 'NotaCredito',
            [FiscalDocumentType.DEBIT_NOTE]: 'NotaDebito',
        }
        return fallbackNames[documentType] ?? 'Comprobante'
    },

    operationReceiptFileName: (operation: SaleOperation): string => {
        const table = saleOperationHelpers.getTable(operation) === TableName.SALE_ORDERS ? 'Venta' : 'Acopio'
        return `Recibo${table}_${operation.code}`
    },
    customerTypePriceListFileName: (name: string): string => {
        return `Lista_de_Precios_${name}`
    },

    creditPaymentReceiptFileName: (creditPaymentCode: number) => `ReciboCobro_${creditPaymentCode}`,
    creditPaymentReceiptFilePath: (
        creditPayment: CreditPayment,
        ext = 'pdf'
    ) => `creditPayments/${creditPayment.id}/receipt/Comprobante-Pago_Cobro-${creditPayment.code}.${ext}`,
    
    getFileExtension: (file: File): string => {
        const map: Record<string, string> = {
            'application/pdf': 'pdf',
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
        }
        return map[file.type] || file.name.split('.').pop() || 'pdf'
    },

    toAccept: (types: FileType[]): string => {
        const map = new Map<FileType, string>()
            .set(FileType.CSV, 'text/csv')
            .set(FileType.PDF, 'application/pdf')
            .set(FileType.JPEG, 'image/jpeg')
            .set(FileType.JPG, 'image/jpg')
            .set(FileType.PNG, 'image/png')
            .set(FileType.XLS, '.xls')
            .set(FileType.XLSX, '.xlsx')
        return types.length > 0 ? types.map(type => map.get(type)).join(',') : '*'
    },
    toFileType: (accept: string): FileType => {
        const map = new Map<string, FileType>()
            .set('text/csv', FileType.CSV)
            .set('application/pdf', FileType.PDF)
            .set('image/jpeg', FileType.JPEG)
            .set('image/jpg', FileType.JPG)
            .set('image/png', FileType.PNG)
            .set('application/vnd.ms-excel', FileType.XLS)
            .set('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', FileType.XLSX)
        return map.get(accept)!
    }
}
